/**
 * Access the Cookies module safely.
 *
 * The purpose of this component is to provide access to a user's
 * cookie values in a way that plays nicely with server-side
 * rendering.  The key point to keep in mind is that the server-side
 * renderer does not have access to user-specific data such as a
 * user's cookie-values.
 *
 * When a page is server-side rendered, the initial client render must
 * match the server-render exactly.  This component makes sure that
 * happens, by doing an initial client-side render with the same
 * limited data the server-side renderer has, immediately followed by
 * a second render with the full cookie data.  (The actual mechanics
 * of initial-render + second-full-render are handled by
 * useRenderState.)  Note: when this component is used on a page
 * that is *not* server-side rendered, the browser will often
 * (usually?) coalesce the two DOM-updates, so users only ever see the
 * full-data version.
 *
 * Clients must set the mockOnFirstRender property, and optionally
 * the placeholder property, to indicate what kind of limited data
 * should be used for the server-side render and initial render.
 * This controls what the end-user sees for the (hopefully)
 * short amount of time until the "full" render is done.  Options:
 *
 *  - MOCK MODE: mockOnFirstRender={true}. In this mode, the child
 *    render prop is called with a default Cookies object, which
 *    simulates a user with "normal" cookie values.  The initial
 *    render will render as if the end-user had these cookie values.
 *    Once CookieProvider has mounted, the child render prop will be
 *    called again with the user's actual cookie values.  This is
 *    the best choice if the default cookie values do not lead to
 *    a misleading render.
 *
 *  - PLACEHOLDER MODE: mockOnFirstRender={false} placeholder={fn}.
 *    In this mode, `placeholder` is rendered in place of the child
 *    render prop until CookieProvider has mounted.  Once
 *    CookieProvider has mounted, the child render prop will be
 *    called and replace `placeholder`.  This is a good choice if
 *    there is useful content to show while cookies are loading, that
 *    cannot be simulated just by setting default cookie values.
 *
 *  - DEFERRED MODE: mockOnFirstRender={false} placeholder={null}.
 *    In this mode, the rendering of the child render prop is deferred
 *    entirely until CookieProvider has mounted.  This means the child
 *    prop is not called at all until CookieProvider has mounted.
 *    This is an appropriate choice for data that is ok to "fly in" to
 *    a page (e.g. an "edit" button that flies in if the cookie shows
 *    the user has edit permissions on a content-page.)
 *
 * This API is based on React's component-based context API.
 *
 * Example:
 *
 *   <CookiesProvider mockOnFirstRender={true}>
 *     {(Cookies) => <div>My cookie is {Cookies.readCookie("monster")}</div>}
 *   </CookiesProvider>
 */

import type * as React from "react";

import {useRenderState} from "@khanacademy/wonder-blocks-core";
import * as Cookies from "@repo/data-cookies/cookies-api";
import {Errors, KAError} from "@repo/logging";

export type CookiesAPI = typeof Cookies;

type MockMode = {
    mockOnFirstRender: true;
    placeholder?: undefined;
    children: (cookies: CookiesAPI) => any;
};

type PlaceholderMode = {
    mockOnFirstRender: false;
    placeholder: (() => React.ReactNode) | null;
    children: (cookies: CookiesAPI) => any;
};

type Props = MockMode | PlaceholderMode;

const CookiesProvider = ({
    children,
    placeholder,
    mockOnFirstRender,
}: Props): React.ReactNode => {
    const renderState = useRenderState();
    if (renderState === "standard") {
        return children(Cookies);
    }
    if (mockOnFirstRender) {
        return children(mockCookies);
    }
    return placeholder?.() ?? null;
};
export default CookiesProvider;

const mockCookies = {
    ...Cookies,
    createCookie: () => {
        throw new KAError(
            "Cannot use createCookie in <CookiesProvider mockOnFirstRender />",
            Errors.Internal,
        );
    },
    readCookie: () => null,
    eraseCookie: () => {
        throw new KAError(
            "Cannot use eraseCookie in <CookiesProvider mockOnFirstRender />",
            Errors.Internal,
        );
    },
    // Not sure if it's right to throw here, or just return false.
    areCookiesEnabled: () => false,
    createCookiesFromURL: () => {
        throw new KAError(
            "Cannot use createCookiesFromURL in <CookiesProvider mockOnFirstRender />",
            Errors.Internal,
        );
    },
    parseConsentCookie: () => {
        throw new KAError(
            "Cannot use parseConsentCookie in <CookiesProvider mockOnFirstRender />",
            Errors.Internal,
        );
    },
} as const;
